import React, { Children, cloneElement, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { getListControllerProps, useDataProvider } from 'react-admin';
import { Card, makeStyles } from '@material-ui/core';

import { ActionToolbar } from 'components/common';
import Pagination from '../pagination/Pagination';
import Actions from './Actions';
import BulkActions from './BulkActions';
import useCustomNotify from 'hooks/useCustomNotify';

const useStyles = makeStyles((theme) => ({
  root: {},

  loadingContainer: {
    textAlign: 'center',
  },
  short: {
    margin: '0 auto',
    maxWidth: theme.spacing(172),
  },

  main: {
    display: 'flex',
    minWidth: 0,
  },

  content: {
    overflow: 'visible',
    marginTop: 0,
    transition: theme.transitions.create('margin-top'),
    position: 'relative',

    flex: '1 1 auto',
    minWidth: 0,
    [theme.breakpoints.down('xs')]: {
      boxShadow: 'none',
    },
  },

  fullWidth: {
    width: 'calc(100vw - 64px)',
  },

  emptyContent: {
    padding: theme.spacing(2),
    width: 'auto',
  },

  emptyContentWithNoMessage: {
    display: 'none',
    width: 'auto',
  },

  actions: {
    zIndex: 2,
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },

  noResults: {
    padding: 20,
  },
}));

const ListView = ({
  className,
  children,
  filters,
  initialFilters,
  bulkActions,
  actions = <Actions />,
  // @ts-ignore
  pagination = <Pagination />,
  emptyMessage = <>No results found</>,
  welcomeMessage,
  filterDefaultValues,
  fullWidth = true,
  ...props
}) => {
  const classes = useStyles();
  const history = useHistory();
  const dataProvider = useDataProvider();
  const notify = useCustomNotify();

  const controllerProps = getListControllerProps(props);

  const [ordersExist, setOrdersExist] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const { loaded, ids, total } = controllerProps;

  useEffect(() => {
    dataProvider
      .get('checkOrders')
      .then(({ data }) => setOrdersExist(data))
      .catch((error) => {
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred while checking orders';
        notify(errorMessage, 'error');
      })
      .finally(() => setLoading(false));
  }, [dataProvider, notify]);

  const isEmpty = loaded && (ids.length === 0 || total === 0);
  const isWelcome = !total && !('q' in controllerProps.filterValues) && !ordersExist;

  useEffect(() => {
    if (history.action === 'POP') return;
    const { setPage, page } = props;
    if (page === 1) return;
    setPage(1);
  }, []); //eslint-disable-line

  useEffect(() => {
    const { setFilters } = props;
    if (filterDefaultValues) setFilters(filterDefaultValues);
  }, []); //eslint-disable-line

  return (
    <div className={classNames(className, classes.root, props.short && classes.short)}>
      {(filters || actions) && (
        <ActionToolbar
          left={
            filters &&
            React.cloneElement(Children.only(filters), {
              ...controllerProps,
              ...filters.props,
              filterDefaultValues,
              initialFilters,
            })
          }>
          {Children.map(actions, (child) =>
            React.cloneElement(child, {
              ...controllerProps,
              ...child.props,
            })
          )}
        </ActionToolbar>
      )}
      <div className={classes.main}>
        <Card
          className={classNames(
            classes.content,
            fullWidth && classes.fullWidth,
            isEmpty && classes.emptyContent,
            isEmpty && emptyMessage === null && classes.emptyContentWithNoMessage
          )}
          key={props.version}>
          {bulkActions && <BulkActions {...controllerProps}>{bulkActions}</BulkActions>}
          {children &&
            cloneElement(Children.only(children), {
              ...controllerProps,
              hasBulkActions: !!bulkActions,
            })}
          {!loading &&
            (isWelcome
              ? welcomeMessage
              : isEmpty
              ? emptyMessage
              : pagination && cloneElement(pagination, controllerProps))}
        </Card>
      </div>
    </div>
  );
};

export default ListView;
